import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import {
  Plus,
  PlusCircle,
  Trash2,
  Shield,
  ShieldCheck,
  ChevronRight,
  ChevronLeft,
  Save,
  AlertCircle,
  CheckCircle2,
  Lock,
  Edit2,
  Settings,
  Check,
  X,
  MoreVertical,
  Users,
  Search,
  AlertTriangle,
  UserPlus,
  Info
} from 'lucide-react';
import { Link, useHistory } from 'react-router-dom';
import { Button, Card, Input, Modal, Breadcrumb, Select, Table } from '../UI';
import { getRoles, addRole, updateRole, deleteRole, getAdmins, addAdmin, removeAdmin } from '../../store/actions';
import { useToast } from '../UI';
import { useDebounce } from '../../hooks/useDebounce';
import { axiosConfig } from '../../config';

const RESOURCE_DEFINITIONS = [
  {
    id: 'sites',
    label: 'Site Management',
    actions: ['create', 'read', 'update', 'delete', 'archive', 'restore']
  },
  {
    id: 'doors',
    label: 'Door Management',
    actions: ['create', 'read', 'update', 'delete', 'archive', 'restore']
  },
  {
    id: 'requests',
    label: 'Access Requests',
    actions: ['create', 'read', 'update', 'delete', 'revoke', 'activate']
  },
  {
    id: 'employees',
    label: 'Employee Directory',
    actions: ['read', 'update']
  },
  {
    id: 'reports',
    label: 'Reports',
    actions: ['read', 'export']
  }
];

const RoleManagement = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { toast } = useToast();
  const roles = useSelector((state: any) => state.auth.roles);
  const admins = useSelector((state: any) => state.auth.admins);

  const [activeTab, setActiveTab] = useState<'roles' | 'members'>('roles');
  const [searchQuery, setSearchQuery] = useState('');
  const [roleChanges, setRoleChanges] = useState<Record<string, string[]>>({});

  // Role Modal State
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<any>(null);
  const [newRoleName, setNewRoleName] = useState('');
  const [editingPermissions, setEditingPermissions] = useState<string[]>([]);
  const [editingScope, setEditingScope] = useState<string[]>([]);
  const [isSavingRole, setIsSavingRole] = useState(false);

  // Member Modal State
  const [isMemberModalOpen, setIsMemberModalOpen] = useState(false);
  const [memberSearchText, setMemberSearchText] = useState('');
  const debouncedMemberSearch = useDebounce(memberSearchText, 500);
  const [employeeItems, setEmployeeItems] = useState<any[]>([]);
  const [selectedMemberId, setSelectedMemberId] = useState('');
  const [selectedMemberRoleIds, setSelectedMemberRoleIds] = useState<string[]>([]);
  const [isAddingMember, setIsAddingMember] = useState(false);

  useEffect(() => {
    dispatch(getRoles() as any);
    dispatch(getAdmins() as any);
  }, [dispatch]);

  // Member Search Effect
  useEffect(() => {
    if (debouncedMemberSearch) {
      axios.get(`/api/requests/search/${debouncedMemberSearch}`, axiosConfig)
        .then((response) => {
          setEmployeeItems(response.data.map((emp: any) => ({
            label: `${emp.oracleId} - ${emp.name}`,
            value: emp.oracleId
          })));
        })
        .catch((err) => console.error("Search member error:", err));
    } else {
      setEmployeeItems([]);
    }
  }, [debouncedMemberSearch]);

  const handleOpenRoleModal = (role: any = null) => {
    if (role) {
      setEditingRole(role);
      setNewRoleName(role.name);
      setEditingPermissions(Array.isArray(role.permissions) ? role.permissions : []);
      setEditingScope(role.resourceScope || []);
    } else {
      setEditingRole(null);
      setNewRoleName('');
      setEditingPermissions([]);
      setEditingScope([]);
    }
    setIsRoleModalOpen(true);
  };

  const handleSaveRole = async () => {
    if (!newRoleName) return toast("Role name is required", "error");
    setIsSavingRole(true);
    try {
      if (editingRole) {
        await dispatch(updateRole(editingRole.id, {
          name: newRoleName,
          permissions: editingPermissions,
          resourceScope: editingScope
        }) as any);
        toast("Role updated successfully", "success");
      } else {
        await dispatch(addRole({
          name: newRoleName,
          permissions: editingPermissions,
          resourceScope: editingScope
        }) as any);
        toast("Role created successfully", "success");
      }
      setIsRoleModalOpen(false);
    } catch (error: any) {
      toast("Failed to save role", "error");
    } finally {
      setIsSavingRole(false);
    }
  };

  const handleDeleteRole = async (id: string, type: string) => {
    if (type === 'SYSTEM') return toast("System roles cannot be deleted", "error");
    if (window.confirm("Are you sure you want to delete this role?")) {
      try {
        await dispatch(deleteRole(id) as any);
        toast("Role deleted successfully", "success");
      } catch (error: any) {
        toast("Failed to delete role", "error");
      }
    }
  };

  const handleAddMember = async () => {
    if (!selectedMemberId || selectedMemberRoleIds.length === 0) return toast("Employee and Role are required", "error");
    setIsAddingMember(true);
    try {
      await dispatch(addAdmin(selectedMemberId, selectedMemberRoleIds) as any);
      toast("Administrator added successfully", "success");
      setIsMemberModalOpen(false);
      dispatch(getAdmins() as any);
      setSelectedMemberId('');
      setSelectedMemberRoleIds([]);
    } catch (error) {
      toast("Failed to add administrator", "error");
    } finally {
      setIsAddingMember(false);
    }
  };

  const handleUpdateUserRoles = async (userId: string, roleIds: string[]) => {
    try {
      await dispatch(addAdmin(userId, roleIds) as any);
      toast("User roles updated", "success");
      dispatch(getAdmins() as any);
    } catch (error) {
      toast("Failed to update user roles", "error");
    }
  };

  const togglePermission = (perm: string) => {
    setEditingPermissions(prev =>
      prev.includes(perm) ? prev.filter(p => p !== perm) : [...prev, perm]
    );
  };

  const toggleResourceActions = (resourceId: string, actions: string[], enable: boolean) => {
    const resourcePerms = actions.map(a => `${a}:${resourceId}`);
    if (enable) {
      setEditingPermissions(prev => Array.from(new Set([...prev, ...resourcePerms])));
    } else {
      setEditingPermissions(prev => prev.filter(p => !resourcePerms.includes(p)));
    }
  };

  const filteredRoles = roles?.filter((r: any) =>
    r.name.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  const filteredAdmins = admins?.filter((a: any) =>
    a.fullName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    a.oracleId.includes(searchQuery)
  ) || [];

  return (
    <div className="space-y-4 animate-in fade-in duration-500">
      {/* Header Section */}
      <div className="flex flex-col gap-2">
        <Breadcrumb
          items={[
            { label: 'Home', to: '/dashboard' },
            { label: 'Role Management' }
          ]}
        />

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 bg-slate-800 rounded-xl flex items-center justify-center">
              <ShieldCheck className="w-4 h-4 text-[#a3d853]" />
            </div>
            <h1 className="text-xl font-bold text-slate-900 tracking-tight leading-none">Role Management</h1>
          </div>

          <div className="flex items-center bg-slate-100 p-0.5 rounded-lg w-fit">
            <button
              onClick={() => setActiveTab('roles')}
              className={`px-4 py-1.5 rounded-md text-xs font-bold transition-all whitespace-nowrap ${activeTab === 'roles' ? 'bg-white text-slate-900 shadow-xs' : 'text-slate-400 hover:text-slate-600'}`}
            >
              Roles
            </button>
            <button
              onClick={() => setActiveTab('members')}
              className={`px-4 py-1.5 rounded-md text-xs font-bold transition-all whitespace-nowrap ${activeTab === 'members' ? 'bg-white text-slate-900 shadow-xs' : 'text-slate-400 hover:text-slate-600'}`}
            >
              Members
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Card */}
      <div className="rounded-xl border border-slate-200/60 shadow-sm overflow-hidden bg-white">
        {/* Table Top Bar */}
        <div className="px-3 py-2.5 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white">
          <div className="relative group flex-1 max-w-xs">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#8dc63f] transition-colors" />
            <input
              type="text"
              placeholder={`Search ${activeTab}...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-3 h-8 w-full bg-slate-50 border border-slate-100 rounded-lg text-xs font-normal focus:bg-white focus:border-[#8dc63f] focus:ring-2 focus:ring-[#8dc63f]/5 transition-all outline-none"
            />
          </div>
          {activeTab === 'roles' ? (
            <Button onClick={() => handleOpenRoleModal()} className="h-10 px-4 gap-1.5 text-xs font-semibold shadow-sm">
              <PlusCircle className="w-3.5 h-3.5" />
              Add Role
            </Button>
          ) : (
            <Button onClick={() => setIsMemberModalOpen(true)} className="h-10 px-3 gap-1.5 text-xs font-semibold shadow-sm">
              <UserPlus className="w-3.5 h-3.5" />
              Add Member
            </Button>
          )}
        </div>

        {/* Roles View */}
        {activeTab === 'roles' && (
          <Table
            rowKey="id"
            dataSource={filteredRoles}
            onRowClick={(role) => handleOpenRoleModal(role)}
            columns={[
              {
                title: 'Role Name',
                key: 'name',
                width: '180px',
                render: (_: any, role: any) => (
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${role.type === 'SYSTEM' ? 'bg-[#8dc63f]/15' : 'bg-[#8dc63f]/5'}`}>
                      <Shield className={`w-4 h-4 ${role.type === 'SYSTEM' ? 'text-[#8dc63f]' : 'text-[#8dc63f]/50'}`} />
                    </div>
                    <span className="text-xs font-semibold text-slate-800 whitespace-nowrap">{role.name}</span>
                  </div>
                ),
              },
              {
                title: 'Type',
                key: 'type',
                width: '120px',
                render: (val: any) => (
                  <span className={`text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap ${val === 'SYSTEM' ? 'text-slate-600' : 'text-slate-500'}`}>
                    {val || 'Custom'}
                  </span>
                ),
              },
              {
                title: 'Members',
                key: 'id',
                width: '150px',
                render: (id: any) => {
                  const roleMembers = admins.filter((a: any) => (a.roleIds || []).includes(id));
                  return (
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {roleMembers.slice(0, 1).map((admin: any) => (
                        <span key={admin.oracleId} className="text-xs font-medium text-slate-700 rounded px-2 py-0.5 whitespace-nowrap">
                          {admin.fullName}
                        </span>
                      ))}
                      {roleMembers.length > 1 && (
                        <span
                          title={roleMembers.slice(1).map((a: any) => a.fullName).join(', ')}
                          className="text-xs font-semibold text-[#8dc63f] rounded px-2 py-0.5 whitespace-nowrap cursor-default"
                        >
                          +{roleMembers.length - 1} more
                        </span>
                      )}
                    </div>
                  );
                },
              },
              {
                title: 'Actions',
                key: 'actions',
                width: '100px',
                className: 'text-right',
                render: (_: any, role: any) => (
                  <div className="flex items-center justify-end gap-2">
                    <Button variant="ghost" size="sm" className="h-9 w-9 p-0 rounded-lg hover:bg-white border border-transparent hover:border-slate-100 shadow-none">
                      <Edit2 className="w-4 h-4 text-[#8dc63f]" />
                    </Button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDeleteRole(role.id, role.type); }}
                      disabled={role.type === 'SYSTEM'}
                      className={`p-2 rounded-lg transition-colors ${role.type === 'SYSTEM' ? 'text-slate-200 cursor-not-allowed' : 'text-rose-400 hover:bg-rose-50 hover:text-rose-600'}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ),
              },
            ]}
          />
        )}

        {/* Members View */}
        {activeTab === 'members' && (
          <Table
            rowKey="oracleId"
            dataSource={filteredAdmins}
            columns={[
              {
                title: 'User Name',
                key: 'fullName',
                render: (val: any, admin: any) => (
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 font-bold text-xs shrink-0">
                      {val?.charAt(0)}
                    </div>
                    <span className="font-medium text-xs text-slate-800 whitespace-nowrap">{val}</span>
                  </div>
                ),
              },
              {
                title: 'Oracle ID',
                key: 'oracleId',
                width: '140px',
                render: (val: any) => (
                  <span className="text-xs font-mono bg-slate-50 px-2 py-1 rounded text-slate-500 border border-slate-100 whitespace-nowrap">{val}</span>
                ),
              },
              {
                title: 'Assigned Role',
                key: 'roleIds',
                width: '260px',
                render: (_: any, admin: any) => (
                  <div className="flex items-center gap-2">
                    <Select
                      multiple={true}
                      searchable={false}
                      value={roleChanges[admin.oracleId] ?? admin.roleIds ?? []}
                      options={roles.map((r: any) => ({ label: r.name, value: r.id }))}
                      onChange={(val) => setRoleChanges(prev => ({ ...prev, [admin.oracleId]: val }))}
                      placeholder="Select Roles..."
                      className="w-44 bg-white"
                    />
                    {roleChanges[admin.oracleId] && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 px-3 text-xs whitespace-nowrap"
                        onClick={() => {
                          handleUpdateUserRoles(admin.oracleId, roleChanges[admin.oracleId]);
                          setRoleChanges(prev => {
                            const newState = { ...prev };
                            delete newState[admin.oracleId];
                            return newState;
                          });
                        }}
                      >
                        Save
                      </Button>
                    )}
                  </div>
                ),
              },
              {
                title: 'Action',
                key: 'action',
                width: '80px',
                className: 'text-right',
                render: (_: any, admin: any) => (
                  <div className="flex justify-end">
                    <button
                      onClick={() => dispatch(removeAdmin(admin.oracleId) as any)}
                      className="p-2 text-rose-400 hover:bg-rose-50 rounded-lg transition-colors"
                      title="Revoke Admin Access"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ),
              },
            ]}
          />
        )}
      </div>

      {/* Role Configuration Modal */}
      <Modal
        isOpen={isRoleModalOpen}
        onClose={() => setIsRoleModalOpen(false)}
        title={editingRole ? `Update Role: ${editingRole.name}` : "Create New Role"}
        size="lg"
      >
        <div className="space-y-8 py-1">
          <div className="space-y-2">
            <label className="text-[10px] font-bold uppercase tracking-widest text-slate-600">Role</label>
            <Input
              placeholder="e.g. Card Activator, Security Admin..."
              value={newRoleName}
              onChange={(e) => setNewRoleName(e.target.value)}
              className="h-12 text-sm font-bold border-slate-200 bg-slate-50/30 focus:bg-white"
            />
          </div>

          <div className="space-y-6">
            <div className="flex items-center justify-between border-b border-slate-100 pb-2">
              <label className="text-[10px] font-bold uppercase
               tracking-widest text-slate-600">Permissions</label>
            </div>

            <div className="space-y-8 max-h-[450px] overflow-y-auto pr-2 custom-scrollbar">
              {RESOURCE_DEFINITIONS.map((resource) => (
                <div key={resource.id} className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-slate-700">{resource.label}</span>
                    <div className="flex gap-4">
                      <button onClick={() => toggleResourceActions(resource.id, resource.actions, true)} className="text-[10px] font-bold text-[#8dc63f] hover:underline">Select All</button>
                      <button onClick={() => toggleResourceActions(resource.id, resource.actions, false)} className="text-[10px] font-bold text-slate-500 hover:underline">Clear</button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                    {resource.actions.map(action => {
                      const permId = `${action}:${resource.id}`;
                      const actionLabel = action;
                      const isSelected = editingPermissions.includes(permId);

                      return (
                        <button
                          key={permId}
                          onClick={() => togglePermission(permId)}
                          className={`flex items-center gap-2 p-3 rounded-xl border transition-all text-left ${isSelected ? 'bg-[#8dc63f]/10 border-[#8dc63f]/40 text-[#8dc63f]/70 shadow-sm' : 'bg-white border-slate-100 text-slate-500 hover:border-slate-200'}`}
                        >
                          <div className={`w-4 h-4 rounded-md flex items-center justify-center transition-all ${isSelected ? 'bg-[#8dc63f]/70 text-white' : 'bg-slate-100 text-transparent'}`}>
                            <Check className="w-2.5 h-2.5" strokeWidth={5} />
                          </div>
                          <span className="text-[10px] font-bold uppercase tracking-tight">{actionLabel}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-6 border-t border-slate-100">
            <Button variant="ghost" onClick={() => setIsRoleModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveRole} isLoading={isSavingRole} className="px-8 h-10 shadow-lg shadow-blue-500/20">
              <Save className="w-4 h-4 mr-2" />
              {editingRole ? "Save Updates" : "Create Role"}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Add Member Modal */}
      <Modal
        isOpen={isMemberModalOpen}
        onClose={() => setIsMemberModalOpen(false)}
        title="Add New Member"
      >
        <div className="space-y-6 py-4">
          <div className="p-4 rounded-2xl border border-#8dc63f] flex gap-4">
            <p className="text-[11px] font-medium text-slate-500 leading-relaxed">
              Grant administrative access to an employee by assigning them a predefined role. This will allow them to manage system components based on the role's permissions.
            </p>
          </div>

          <div className="space-y-4">
            <Select
              label="Select Employee"
              placeholder="Search by Name or Oracle ID..."
              options={employeeItems}
              onSearch={(val) => setMemberSearchText(val)}
              onChange={(val) => setSelectedMemberId(val)}
              value={selectedMemberId}
              required
            />

            <Select
              label="Assign Roles"
              placeholder="Select roles..."
              multiple={true}
              options={roles.map((r: any) => ({ label: r.name, value: r.id }))}
              onChange={(val) => setSelectedMemberRoleIds(val)}
              value={selectedMemberRoleIds}
              required
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
            <Button variant="ghost" onClick={() => setIsMemberModalOpen(false)}>Cancel</Button>
            <Button onClick={handleAddMember} isLoading={isAddingMember} className="px-8 h-12 shadow-lg shadow-blue-500/20">
              <UserPlus className="w-4 h-4 mr-2" />
              Grant Access
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default RoleManagement;



